import deploymentCatalog from '../../config/shigo-deployments.json';

export enum ShigoDeploymentTarget {
  Development = 'dev',
  Production = 'prod',
}

export enum ShigoNetwork {
  BenefitAds = 'benefit-ads',
  Oveeo = 'oveeo',
  Shigo = 'shigo',
  Kiwi = 'kiwi',
  Tviq = 'tviq',
  DxTech = 'dxtech',
  Altentiv = 'altentiv',
  Demo = 'demo',
  EmtvSsp = 'emtvssp',
}

export interface ShigoDeploymentProfile {
  production: boolean;
  apiBaseUrl: string;
  reportingServiceUrl: string;
  networkId: string;
  network: ShigoNetwork;
  target: ShigoDeploymentTarget;
  displayName: string;
  customFlag: string;
  vastUrl: string;
  cdnUrl: string;
  a2vUrl: string;
  defaultBidderCode: string;
  vastEndpoint: string;
  exchangeDomainUrl: string;
  defaultAdsTxtDomain: string;
  defaultAdsTxtTagID: string;
  sellerDomainLabel: string;
  logoUrl: string;
  logoLightUrl: string;
  faviconUrl: string;
  madBuyServiceUrl: string;
  madSenseServiceUrl: string;
  madSenseReportingUrl: string;
  madSenseCompanyId: string;
  kubernetesScope: string;
}

type CatalogNetworkProfile = Omit<
  ShigoDeploymentProfile,
  | 'apiBaseUrl'
  | 'reportingServiceUrl'
  | 'networkId'
  | 'network'
  | 'target'
  | 'displayName'
  | 'kubernetesScope'
> & {
  networkId?: number;
  serviceUrl: string;
  reportServiceUrl: string;
  platformTitle: string;
};

type CatalogNetwork = {
  deployableTargets: ShigoDeploymentTarget[];
  kubernetesScope?: string;
  profiles: Record<ShigoDeploymentTarget, CatalogNetworkProfile>;
};

type ShigoDeploymentCatalog = {
  networks: Record<ShigoNetwork, CatalogNetwork>;
};

const catalog = deploymentCatalog as ShigoDeploymentCatalog;

const shigoNetworks = Object.values(ShigoNetwork);
const shigoDeploymentTargets = Object.values(ShigoDeploymentTarget);

export function resolveShigoDeploymentProfile(
  target: ShigoDeploymentTarget,
  network: ShigoNetwork,
): ShigoDeploymentProfile {
  const networkDefinition = catalog.networks[network];
  const profile = networkDefinition.profiles[target];
  return {
    ...profile,
    apiBaseUrl: profile.serviceUrl,
    reportingServiceUrl: profile.reportServiceUrl,
    networkId: profile.networkId === undefined ? '' : String(profile.networkId),
    network,
    target,
    displayName: profile.platformTitle,
    kubernetesScope: networkDefinition.kubernetesScope ?? `network-${network}`,
  };
}

export function shigoNetworksForTarget(target: ShigoDeploymentTarget) {
  return shigoNetworks.filter((network) =>
    catalog.networks[network].deployableTargets.includes(target),
  );
}

export function isShigoDeploymentTarget(value: string | undefined): value is ShigoDeploymentTarget {
  return shigoDeploymentTargets.includes(value as ShigoDeploymentTarget);
}

export function isShigoNetwork(value: string | undefined): value is ShigoNetwork {
  return shigoNetworks.includes(value as ShigoNetwork);
}
